// Vue & Plugins
import { MaybeRefOrGetter, toValue } from 'vue'

// Models
import { SiteID } from '../../sites/models/site'
import { keepPreviousData, queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { Shift, ShiftID } from '../models/shift'
import { cancel, create, createBulk, destroy, fetchActivity, publish, publishBulk, remind, ShiftCreateForm, ShiftPublishBulkForm, ShiftUpdateForm, show, skillIndex, skillSync, unpublish, update } from '../shiftService'
import { useEventStore } from '../../common/eventStore'
import { ToastEvent } from '../../common/models/toastEvent'
import { Skill, SkillID } from '../../skills/models/skill'
import { Pagination } from '../../common/models/pagination'

interface ShiftFilters {
    name?: MaybeRefOrGetter<string>
    perPage?: MaybeRefOrGetter<number>
    page?: MaybeRefOrGetter<number>
}

export const shiftKeys = {
    all: ['shifts'] as const,
    lists: () => [...shiftKeys.all, 'list'] as const,
    list: (filters: ShiftFilters) => [...shiftKeys.lists(), { name: filters.name, perPage: filters.perPage, page: filters.page }] as const,
    details: () => [...shiftKeys.all, 'detail'] as const,
    detail: (id: MaybeRefOrGetter) => [...shiftKeys.details(), id] as const,
}

interface ShiftSkillFilters {
    shiftId?: MaybeRefOrGetter<ShiftID>
    perPage?: MaybeRefOrGetter<number>
    page?: MaybeRefOrGetter<number>
}

export const shiftSkillKeys = {
    all: ['shifts-skills'] as const,
    lists: () => [...shiftSkillKeys.all, 'list'] as const,
    list: (filters: ShiftSkillFilters) => [...shiftSkillKeys.lists(), { shift_id: filters.shiftId, perPage: filters.perPage, page: filters.page }] as const,
}

export function shiftDetailOptions(siteId: MaybeRefOrGetter<SiteID>, shiftId: MaybeRefOrGetter<ShiftID>) {
    return queryOptions({
        queryKey: shiftKeys.detail(shiftId),
        queryFn: async () => {
            return show(toValue(siteId), toValue(shiftId))
        },
    })
}

export function useShiftDetailQuery(siteId: MaybeRefOrGetter<SiteID>, shiftId: MaybeRefOrGetter<ShiftID>) {
    return useQuery(shiftDetailOptions(siteId, shiftId))
}

export function useShiftCreateMutation(siteId: MaybeRefOrGetter<SiteID>) {
    const eventStore = useEventStore()

    return useMutation<{ shift: Shift }, Error, { form: ShiftCreateForm }>({
        mutationFn: async (shiftCreate) => {
            return create(toValue(siteId), shiftCreate.form)
        },
        onSettled: () => {
            console.log('settled')
        },
        onSuccess: async () => {
            eventStore.addCreateToast()
        }
    })
}

export function useShiftCreateBulkMutation(siteId: MaybeRefOrGetter<SiteID>) {
    const eventStore = useEventStore()

    return useMutation<{ shifts: Shift[] }, Error, { form: ShiftCreateForm }>({
        mutationFn: async (shiftCreate) => {
            return createBulk(toValue(siteId), shiftCreate.form)
        },
        onSettled: () => {
            console.log('settled')
        },
        onSuccess: async () => {
            eventStore.addToast({ severity: 'success', summary: 'Created' })
        },
        onError: async (error) => {
            eventStore.addToast({ severity: 'error', summary: 'Error', detail: error.message })
        },
    })
}

export function useShiftUpdateMutation(siteId: MaybeRefOrGetter<SiteID>) {
    const queryClient = useQueryClient()
    const eventStore = useEventStore()

    return useMutation<{ shift: Shift }, Error, { form: ShiftUpdateForm }>({
        mutationFn: async (shiftUpdate) => {
            return update(toValue(siteId), toValue(shiftUpdate.form.shiftId), shiftUpdate.form)
        },
        onSettled: async (_data, _error, variables) => {
            queryClient.invalidateQueries({ queryKey: shiftKeys.lists() })

            return await queryClient.invalidateQueries({ queryKey: shiftKeys.detail(variables.form.shiftId) })
        },
        onSuccess: async () => {
            eventStore.addUpdateToast()
        },
        onError: async (error) => {
            eventStore.addErrorToast({ detail: error.message })
        },
    })
}

export function useShiftPublishMutation(siteId: MaybeRefOrGetter<SiteID>) {
    const queryClient = useQueryClient()
    const eventStore = useEventStore()

    return useMutation<void, Error, { form: { shiftId: ShiftID } }>({
        mutationFn: async (data) => {
            return publish(toValue(siteId), data.form.shiftId)
        },
        onSettled: async (_data, _error, variables) => {
            queryClient.invalidateQueries({ queryKey: shiftKeys.lists() })

            return await queryClient.invalidateQueries({ queryKey: shiftKeys.detail(variables.form.shiftId) })
        },
        onSuccess: async () => {
            const event = new ToastEvent('Published')
            eventStore.addEvent(event)
        },
        onError: async () => {
            const event = new ToastEvent('Failed to Publish', 'error')
            eventStore.addEvent(event)
        },
    })
}

export function useShiftUnpublishMutation(siteId: MaybeRefOrGetter<SiteID>) {
    const queryClient = useQueryClient()
    const eventStore = useEventStore()

    return useMutation<void, Error, { form: { shiftId: ShiftID } }>({
        mutationFn: async (data) => {
            return unpublish(toValue(siteId), data.form.shiftId)
        },
        onSettled: async (_data, _error, variables) => {
            queryClient.invalidateQueries({ queryKey: shiftKeys.lists() })

            return await queryClient.invalidateQueries({ queryKey: shiftKeys.detail(variables.form.shiftId) })
        },
        onSuccess: async () => {
            const event = new ToastEvent('Unpublished')
            eventStore.addEvent(event)
        },
        onError: async () => {
            const event = new ToastEvent('Failed to unpublish', 'error')
            eventStore.addEvent(event)
        },
    })
}

export function useShiftPublishBulkMutation(siteId: MaybeRefOrGetter<SiteID>) {
    const queryClient = useQueryClient()
    const eventStore = useEventStore()

    return useMutation<void, Error, { form: ShiftPublishBulkForm }>({
        mutationFn: async (data) => {
            return publishBulk(toValue(siteId), data.form)
        },
        onSettled: async () => {
            queryClient.invalidateQueries({ queryKey: shiftKeys.lists() })
        },
        onSuccess: async () => {
            const event = new ToastEvent('Published')
            eventStore.addEvent(event)
        },
        onError: async () => {
            const event = new ToastEvent('Failed to Publish', 'error')
            eventStore.addEvent(event)
        },
    })
}

export function useShiftCancelMutation(siteId: MaybeRefOrGetter<SiteID>) {
    const queryClient = useQueryClient()
    const eventStore = useEventStore()

    return useMutation<void, Error, { shiftId: ShiftID }>({
        mutationFn: async (data) => {
            return cancel(toValue(siteId), data.shiftId)
        },
        onSettled: async () => {
            queryClient.invalidateQueries({ queryKey: shiftKeys.lists() })
        },
        onSuccess: async () => {
            eventStore.addToast({ summary: 'Cancelled' })
        },
        onError: async (error) => {
            eventStore.addErrorToast({ detail: error.message })
        },
    })
}

export function useShiftDeleteMutation(siteId: MaybeRefOrGetter<SiteID>) {
    const queryClient = useQueryClient()
    const eventStore = useEventStore()

    return useMutation<void, Error, { shiftId: ShiftID }>({
        mutationFn: async (data) => {
            return destroy(toValue(siteId), data.shiftId)
        },
        onSettled: async () => {
            queryClient.invalidateQueries({ queryKey: shiftKeys.lists() })
        },
        onSuccess: async () => {
            eventStore.addToast({ summary: 'Deleted' })
        },
        onError: async (error) => {
            eventStore.addErrorToast({ detail: error.message })
        },
    })
}

export function useShiftSkillQuery(siteId: MaybeRefOrGetter<SiteID>, shiftId: MaybeRefOrGetter<ShiftID>) {
    return useQuery<{ skills: Skill[], pagination: Pagination }, Error>({
        queryKey: shiftSkillKeys.list({ shiftId }),
        queryFn: async () => {
            return skillIndex(toValue(siteId), toValue(shiftId))
        },
        placeholderData: keepPreviousData,
    })
}

export function useShiftSkillSyncMutation(siteId: MaybeRefOrGetter<SiteID>) {
    const queryClient = useQueryClient()
    const eventStore = useEventStore()

    return useMutation<void, Error, { shiftId: ShiftID, form: { skillIds: SkillID[] } }>({
        mutationFn: async (data) => {
            return skillSync(toValue(siteId), data.shiftId, data.form)
        },
        onSettled: async () => {
            queryClient.invalidateQueries({ queryKey: shiftKeys.lists() })
        },
        onSuccess: async () => {
            eventStore.addToast({ summary: 'Updated' })
        },
        onError: async (error) => {
            eventStore.addErrorToast({ detail: error.message })
        },
    })
}


export function useActivityByShift(siteId: MaybeRefOrGetter<SiteID>, shiftId: MaybeRefOrGetter<ShiftID>) {
    return useQuery({
        queryKey: shiftKeys.lists(),
        queryFn: async () => {
            return fetchActivity(toValue(siteId), toValue(shiftId))
        },
        placeholderData: keepPreviousData,
    })
}

export function useShiftRemindMutation(siteId: MaybeRefOrGetter<SiteID>) {
    const queryClient = useQueryClient()
    const eventStore = useEventStore()

    return useMutation<void, Error, { shiftId: ShiftID, form: { method: 'push' | 'email' | 'sms' } }>({
        mutationFn: async (data) => {
            return remind(toValue(siteId), data.shiftId, { method: data.form.method })
        },
        onSettled: async (_data, _error, variables) => {
            return await queryClient.invalidateQueries({ queryKey: shiftKeys.detail(variables.shiftId) })
        },
        onSuccess: async () => {
            eventStore.addToast({ summary: 'Reminder Sent' })
        },
        onError: async (error) => {
            eventStore.addErrorToast({ detail: error.message })
        },
    })
}